import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import { toast } from 'react-toastify'

import { httpOk, GET, POST, PUT, FILE, FILE_D } from 'helpers/api'
import { formatDate } from 'helpers/helpers'
import SmsCounter from 'pages/parts/SmsCounter'

// Каталог SMS-шаблонов Mison SMS.
// Оператор (SMSFLY) берёт обычную цену только за текст, зарегистрированный у него как шаблон;
// любой другой — ~3500 сум. Здесь Mison ведёт тексты, отдаёт их оператору и отмечает статус.
// Заведения видят и отправляют только «Зарегистрирован» (коды входа — в любом статусе).
// Правка текста зарегистрированного шаблона сбрасывает статус в «Черновик» — на сервере.

const LOCALES = [
	{ code: 'ru', label: 'Русский' },
	{ code: 'uz_latn', label: "O'zbekcha" },
]

const STATUSES = [
	{ value: 'draft', label: 'Черновик' },
	{ value: 'submitted', label: 'Отдан оператору' },
	{ value: 'approved', label: 'Зарегистрирован' },
	{ value: 'rejected', label: 'Отклонён' },
]

const USAGES = [
	{ value: 'marketing', label: 'Рассылки и RFM' },
	{ value: 'callcenter', label: 'Колл-центр' },
	{ value: 'debt', label: 'Долг клиента' },
	{ value: 'debt_org', label: 'Долг организации' },
	{ value: 'auto', label: 'Автоматические гостю' },
	{ value: 'staff', label: 'Сотрудникам' },
	{ value: 'system', label: 'Системные (платит Mison)' },
]

const emptyNew = {
	code: '',
	usage: 'marketing',
	group_code: '',
	title: '',
	texts: { ru: '', uz_latn: '' },
}

function statusLabel(status) {
	return STATUSES.find(item => item.value === status)?.label || '—'
}

function returnClassStatus(status) {
	if (status === 'approved') return 'bg-success text-white'
	if (status === 'submitted') return 'bg-warning'
	if (status === 'rejected') return 'bg-danger text-white'
	return ''
}

function usageLabel(usage) {
	return USAGES.find(item => item.value === usage)?.label || usage
}

function Templates() {
	const [data, setData] = useState([])
	const [variables, setVariables] = useState([])
	const [filter, setFilter] = useState({ usage: null, status: null, search: '' })
	const [checked, setChecked] = useState([])
	const [modal, setModal] = useState({ bool: false, item: null })
	const [modalNew, setModalNew] = useState({ bool: false, item: emptyNew })
	const [showVariables, setShowVariables] = useState(false)
	const [importResult, setImportResult] = useState({ bool: false, summary: null, errors: [] })

	async function loadData() {
		const response = await GET('/admin/sms-templates', {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data?.data || [])
			setVariables(response.data?.variables || [])
		}
	}

	const filtered = useMemo(() => {
		const search = filter.search.trim().toLowerCase()
		return data.filter(item => {
			if (filter.usage && item.usage !== filter.usage) return false
			if (filter.status && !Object.values(item.texts).some(text => text.status === filter.status)) return false
			if (search) {
				const haystack = [item.title, item.code, ...Object.values(item.texts).map(text => text.text)].join(' ').toLowerCase()
				if (!haystack.includes(search)) return false
			}
			return true
		})
	}, [data, filter])

	const counters = useMemo(() => {
		const result = { draft: 0, submitted: 0, approved: 0, rejected: 0 }
		data.forEach(item => Object.values(item.texts).forEach(text => {
			result[text.status] = (result[text.status] || 0) + 1
		}))
		return result
	}, [data])

	// Список для оператора: каждый текст отдельной строкой, переменные — в фигурных скобках
	function operatorList(items) {
		const lines = []
		let index = 1
		items.forEach(item => {
			LOCALES.forEach(locale => {
				const text = item.texts[locale.code]
				if (!text) return
				lines.push(`${index}. [${locale.code === 'ru' ? 'RU' : 'UZ'}] ${text.text}`)
				index++
			})
		})
		return lines
	}

	function itemsForOperator() {
		const source = checked.length > 0 ? data.filter(item => checked.includes(item.id)) : filtered
		return source.filter(item => item.is_active)
	}

	async function copyForOperator() {
		const lines = operatorList(itemsForOperator())
		const header = 'Шаблоны SMS Mison. Переменная часть — в фигурных скобках, например {name} — имя гостя, {brand} — название заведения.'
		try {
			await navigator.clipboard.writeText([header, '', ...lines].join('\n'))
			toast.success(`Скопировано шаблонов: ${lines.length}`)
		} catch (e) {
			toast.error('Не удалось скопировать — скачайте файл')
		}
	}

	// Excel: лист «Шаблоны» (править и загружать обратно) + «Для оператора» (отдать SMSFLY).
	// Выбраны галочки — только они, иначе все включённые
	async function downloadExcel() {
		const query = checked.length > 0 ? `?ids=${checked.join(',')}` : ''
		const response = await FILE_D(`/admin/sms-templates/export${query}`, { loader: true })
		if (httpOk(response)) {
			const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
			const url = URL.createObjectURL(file)
			const link = document.createElement('a')
			link.href = url
			link.setAttribute('download', 'mison_sms_templates.xlsx')
			document.body.appendChild(link)
			link.click()
			link.remove()
			URL.revokeObjectURL(url)
		}
	}

	// Загрузка листа «Шаблоны»: сервер проверяет весь файл, при ошибке не меняет ничего
	async function uploadExcel(e) {
		const file = e.target.files?.[0]
		e.target.value = ''
		if (!file) return

		const formData = new FormData()
		formData.append('file', file)
		const response = await FILE('/admin/sms-templates/import', formData, { loader: true })

		if (httpOk(response)) {
			setImportResult({ bool: true, summary: response.data, errors: [] })
			loadData()
		} else if (response?.data?.errors) {
			setImportResult({ bool: true, summary: null, errors: response.data.errors })
		}
	}

	async function bulkStatus(status) {
		const textIds = []
		data.filter(item => checked.includes(item.id)).forEach(item => {
			Object.values(item.texts).forEach(text => textIds.push(text.id))
		})
		if (textIds.length === 0) return

		const response = await PUT('/admin/sms-templates/status', { text_ids: textIds, status }, { loader: true })
		if (httpOk(response)) {
			setChecked([])
			loadData()
		}
	}

	function toggleChecked(id) {
		setChecked(checked.includes(id) ? checked.filter(item => item !== id) : [...checked, id])
	}

	function toggleCheckedAll() {
		const ids = filtered.map(item => item.id)
		const allChecked = ids.length > 0 && ids.every(id => checked.includes(id))
		setChecked(allChecked ? checked.filter(id => !ids.includes(id)) : Array.from(new Set([...checked, ...ids])))
	}

	function openEdit(item) {
		const texts = {}
		LOCALES.forEach(locale => {
			const text = item.texts[locale.code]
			texts[locale.code] = {
				text: text?.text || '',
				status: text?.status || 'draft',
				operator_template_id: text?.operator_template_id || '',
				note: text?.note || '',
				original: text || null,
			}
		})
		setModal({ bool: true, item: { ...item, texts } })
	}

	async function saveEdit(e) {
		e.preventDefault()
		const item = modal.item

		let response = await PUT('/admin/sms-templates', {
			id: item.id,
			title: item.title,
			description: item.description || null,
			is_active: item.is_active,
		}, { loader: true, notification: false })
		if (!httpOk(response)) return

		for (const locale of LOCALES) {
			const text = item.texts[locale.code]
			const original = text.original
			const changed = !original
				|| original.text !== text.text
				|| original.status !== text.status
				|| (original.operator_template_id || '') !== text.operator_template_id
				|| (original.note || '') !== text.note
			if (!changed || text.text.trim() === '') continue

			response = await PUT('/admin/sms-templates/text', {
				template_id: item.id,
				locale: locale.code,
				text: text.text,
				status: text.status,
				operator_template_id: text.operator_template_id || null,
				note: text.note || null,
			}, { loader: true, notification: false })
			if (!httpOk(response)) return
			if (response.data?.message) toast.warning(`${locale.label}: ${response.data.message}`)
		}

		toast.success('Сохранено')
		setModal({ bool: false, item: null })
		loadData()
	}

	function patchText(localeCode, patch) {
		setModal({
			...modal,
			item: {
				...modal.item,
				texts: { ...modal.item.texts, [localeCode]: { ...modal.item.texts[localeCode], ...patch } },
			},
		})
	}

	async function saveNew(e) {
		e.preventDefault()
		const response = await POST('/admin/sms-templates', modalNew.item, { loader: true })
		if (httpOk(response)) {
			setModalNew({ bool: false, item: emptyNew })
			loadData()
		}
	}

	useEffect(() => {
		loadData()
	}, [])

	const filteredIds = filtered.map(item => item.id)
	const isAllChecked = filteredIds.length > 0 && filteredIds.every(id => checked.includes(id))

	return (
		<>
			<div className="page-header d-flex justify-content-between flex-wrap gap-2">
				<h4 className="vertical-center">Шаблоны SMS [{data.length}]</h4>
				<div className="d-flex gap-2 flex-wrap">
					<button className="btn-rounded btn btn-outline-primary" onClick={copyForOperator}>
						<i className="uil-copy me-1" />Скопировать для SMSFLY
					</button>
					<button className="btn-rounded btn btn-outline-primary" onClick={downloadExcel}>
						<i className="uil-file-download me-1" />Скачать Excel{checked.length > 0 ? ` (${checked.length})` : ''}
					</button>
					<label className="btn-rounded btn btn-outline-primary mb-0">
						<i className="uil-file-upload me-1" />Загрузить Excel
						<input type="file" accept=".xlsx,.xls" className="d-none" onChange={uploadExcel} />
					</label>
					<button className="btn-rounded btn btn-primary" onClick={() => setModalNew({ bool: true, item: emptyNew })}>
						Создать шаблон
					</button>
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<div className="mb-2" style={{ fontSize: '13px' }}>
						Mison SMS отправляет только тексты в статусе <b>«Зарегистрирован»</b>: за любой другой текст
						оператор берёт ~3500 сум вместо 180. Коды входа гостя и сотрудника уходят в любом статусе — иначе
						гости не войдут на сайт, пока шаблон регистрируется. Порядок: скопировать тексты → отдать в SMSFLY →
						отметить «Отдан оператору» → когда оператор добавил, отметить «Зарегистрирован».
						Изменили текст зарегистрированного шаблона — статус сбросится в «Черновик», его нужно регистрировать заново.
					</div>
					<div className="d-flex gap-3 flex-wrap" style={{ fontSize: '13px' }}>
						<span>Черновик: <b>{counters.draft}</b></span>
						<span>Отдан оператору: <b>{counters.submitted}</b></span>
						<span className="text-success">Зарегистрирован: <b>{counters.approved}</b></span>
						<span className="text-danger">Отклонён: <b>{counters.rejected}</b></span>
						<span role="button" className="text-primary" onClick={() => setShowVariables(!showVariables)}>
							{showVariables ? 'Скрыть переменные' : 'Переменные шаблонов'}
						</span>
					</div>
					{showVariables &&
						<table className="table table-bordered table-sm mt-2 mb-0" style={{ fontSize: '12px' }}>
							<thead>
								<tr>
									<th>Переменная</th>
									<th>Что подставляется</th>
									<th className="text-center">Макс. символов</th>
									<th className="text-center">Кто заполняет</th>
								</tr>
							</thead>
							<tbody>
								{variables.map(variable => (
									<tr key={variable.name}>
										<td><code>{`{${variable.name}}`}</code></td>
										<td>{variable.label}</td>
										<td className="text-center">{variable.max}</td>
										<td className="text-center">{variable.is_input ? 'Заведение при отправке' : 'Система'}</td>
									</tr>
								))}
							</tbody>
						</table>
					}
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex gap-2 mb-3 flex-wrap">
						<div style={{ width: '220px' }}>
							<Select
								options={USAGES}
								value={USAGES.find(item => item.value === filter.usage) || null}
								onChange={(option) => setFilter({ ...filter, usage: option ? option.value : null })}
								placeholder="Назначение"
								isClearable
							/>
						</div>
						<div style={{ width: '200px' }}>
							<Select
								options={STATUSES}
								value={STATUSES.find(item => item.value === filter.status) || null}
								onChange={(option) => setFilter({ ...filter, status: option ? option.value : null })}
								placeholder="Статус"
								isClearable
							/>
						</div>
						<input type="text" className="form-control" style={{ width: '260px' }}
							placeholder="Поиск по названию и тексту"
							value={filter.search}
							onChange={(e) => setFilter({ ...filter, search: e.target.value })} />
						{checked.length > 0 &&
							<div className="d-flex gap-2 align-items-center">
								<span style={{ fontSize: '13px' }}>Выбрано: <b>{checked.length}</b></span>
								<button className="btn btn-sm btn-outline-warning" onClick={() => bulkStatus('submitted')}>Отдан оператору</button>
								<button className="btn btn-sm btn-outline-success" onClick={() => bulkStatus('approved')}>Зарегистрирован</button>
								<button className="btn btn-sm btn-outline-secondary" onClick={() => bulkStatus('draft')}>Черновик</button>
							</div>
						}
					</div>

					<div className="table-responsive">
						<table className="table table-bordered" style={{ fontSize: '13px' }}>
							<thead>
								<tr>
									<th className="text-center" style={{ width: '36px' }}>
										<input type="checkbox" className="form-check-input" checked={isAllChecked} onChange={toggleCheckedAll} />
									</th>
									<th style={{ width: '18%' }}>Шаблон</th>
									<th style={{ width: '11%' }}>Назначение</th>
									<th>Русский</th>
									<th className="text-center" style={{ width: '9%' }}>Статус</th>
									<th>O'zbekcha</th>
									<th className="text-center" style={{ width: '9%' }}>Статус</th>
									<th style={{ width: '50px' }}></th>
								</tr>
							</thead>
							<tbody>
								{filtered.map(item => (
									<tr key={item.id} className={item.is_active ? '' : 'text-muted'}>
										<td className="text-center">
											<input type="checkbox" className="form-check-input"
												checked={checked.includes(item.id)}
												onChange={() => toggleChecked(item.id)} />
										</td>
										<td>
											<div className="fw-bold">{item.title}</div>
											<div className="text-muted" style={{ fontSize: '11px' }}>{item.code}</div>
											{item.is_critical && <div className="text-primary" style={{ fontSize: '11px' }}>уходит в любом статусе</div>}
											{!item.is_active && <div style={{ fontSize: '11px' }}>выключен</div>}
										</td>
										<td>{usageLabel(item.usage)}</td>
										{LOCALES.map(locale => {
											const text = item.texts[locale.code]
											return (
												<React.Fragment key={locale.code}>
													<td style={{ whiteSpace: 'pre-wrap' }}>
														{text?.text || <span className="text-muted">нет текста</span>}
														{text && <div className="text-muted" style={{ fontSize: '11px' }}>{text.length} симв. · {text.segments} SMS</div>}
													</td>
													<td className={`text-center ${returnClassStatus(text?.status)}`}
														title={text?.note || (text?.approved_at ? `Зарегистрирован ${formatDate(text.approved_at)}` : '')}>
														{text ? statusLabel(text.status) : '—'}
													</td>
												</React.Fragment>
											)
										})}
										<td className="text-center">
											<button className="btn btn-sm btn-outline-dark" onClick={() => openEdit(item)}>
												<i className="uil-edit-alt" />
											</button>
										</td>
									</tr>
								))}
								{filtered.length === 0 &&
									<tr><td colSpan="8" className="text-center text-muted">Нет шаблонов</td></tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* MODAL EDIT */}
			<Modal show={modal.bool} animation={false} centered scrollable size="xl"
				onHide={() => setModal({ bool: false, item: null })}>
				<Modal.Header closeButton>
					<Modal.Title>{modal.item?.title}</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '600px' }}>
					{modal.item &&
						<form id="smsTemplateEdit" onSubmit={saveEdit} autoComplete="off">
							<div className="row">
								<div className="col-md-8">
									<div className="form-group">
										<label>Название (видят заведения)<span className="required-mark">*</span></label>
										<input type="text" className="form-control" required maxLength={190}
											value={modal.item.title}
											onChange={(e) => setModal({ ...modal, item: { ...modal.item, title: e.target.value } })} />
									</div>
								</div>
								<div className="col-md-4 d-flex align-items-end">
									<div className="form-check form-switch form-switch-lg mb-3">
										<input type="checkbox" className="form-check-input" id="templateActive"
											checked={!!modal.item.is_active}
											onChange={(e) => setModal({ ...modal, item: { ...modal.item, is_active: e.target.checked } })} />
										<label className="form-check-label" htmlFor="templateActive">Включён</label>
									</div>
								</div>
							</div>
							<div className="text-muted mb-3" style={{ fontSize: '12px' }}>
								Код: <b>{modal.item.code}</b> · {usageLabel(modal.item.usage)}
								{modal.item.event_code && <> · событие <b>{modal.item.event_code}</b></>}
							</div>

							<div className="row">
								{LOCALES.map(locale => {
									const text = modal.item.texts[locale.code]
									return (
										<div className="col-md-6" key={locale.code}>
											<h6>{locale.label}</h6>
											<div className="form-group">
												<label>Текст</label>
												<textarea className="form-control" rows="5" maxLength={670}
													value={text.text}
													onChange={(e) => patchText(locale.code, { text: e.target.value })} />
												<SmsCounter text={text.text} />
											</div>
											<div className="form-group">
												<label>Статус у оператора</label>
												<Select
													options={STATUSES}
													value={STATUSES.find(item => item.value === text.status)}
													onChange={(option) => patchText(locale.code, { status: option.value })}
													isSearchable={false}
												/>
											</div>
											<div className="form-group">
												<label>ID шаблона у оператора</label>
												<input type="text" className="form-control" maxLength={100}
													placeholder="если оператор выдал номер"
													value={text.operator_template_id}
													onChange={(e) => patchText(locale.code, { operator_template_id: e.target.value })} />
											</div>
											<div className="form-group">
												<label>Заметка</label>
												<input type="text" className="form-control" maxLength={1000}
													placeholder="например, причина отказа"
													value={text.note}
													onChange={(e) => patchText(locale.code, { note: e.target.value })} />
											</div>
										</div>
									)
								})}
							</div>
						</form>
					}
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100">
						<button type="button" className="btn btn-outline-warning w-100" onClick={() => setModal({ bool: false, item: null })}>
							Отмена
						</button>
						<button type="submit" form="smsTemplateEdit" className="btn btn-primary w-100">Сохранить</button>
					</div>
				</Modal.Footer>
			</Modal>
			{/* MODAL EDIT */}

			{/* MODAL NEW */}
			<Modal show={modalNew.bool} animation={false} centered scrollable size="lg" backdrop="static"
				onHide={() => setModalNew({ bool: false, item: emptyNew })}>
				<Modal.Header closeButton>
					<Modal.Title>Новый шаблон SMS</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '560px' }}>
					<form id="smsTemplateNew" onSubmit={saveNew} autoComplete="off">
						<div className="row">
							<div className="col-md-6">
								<div className="form-group">
									<label>Код<span className="required-mark">*</span></label>
									<input type="text" className="form-control" required maxLength={80}
										placeholder="например, birthday.cashback"
										value={modalNew.item.code}
										onChange={(e) => setModalNew({ ...modalNew, item: { ...modalNew.item, code: e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, '') } })} />
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label>Группа<span className="required-mark">*</span></label>
									<input type="text" className="form-control" required maxLength={40}
										placeholder="return, loyalty, birthday, promo…"
										value={modalNew.item.group_code}
										onChange={(e) => setModalNew({ ...modalNew, item: { ...modalNew.item, group_code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') } })} />
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label>Назначение<span className="required-mark">*</span></label>
									<Select
										options={USAGES.filter(item => ['marketing', 'callcenter', 'debt', 'debt_org'].includes(item.value))}
										value={USAGES.find(item => item.value === modalNew.item.usage)}
										onChange={(option) => setModalNew({ ...modalNew, item: { ...modalNew.item, usage: option.value } })}
										isSearchable={false}
									/>
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label>Название (видят заведения)<span className="required-mark">*</span></label>
									<input type="text" className="form-control" required maxLength={190}
										value={modalNew.item.title}
										onChange={(e) => setModalNew({ ...modalNew, item: { ...modalNew.item, title: e.target.value } })} />
								</div>
							</div>
							{LOCALES.map(locale => (
								<div className="col-md-6" key={locale.code}>
									<div className="form-group">
										<label>Текст — {locale.label}</label>
										<textarea className="form-control" rows="5" maxLength={670}
											placeholder="{brand}: {name}, …"
											value={modalNew.item.texts[locale.code]}
											onChange={(e) => setModalNew({ ...modalNew, item: { ...modalNew.item, texts: { ...modalNew.item.texts, [locale.code]: e.target.value } } })} />
										<SmsCounter text={modalNew.item.texts[locale.code]} />
									</div>
								</div>
							))}
						</div>
						<div className="text-muted" style={{ fontSize: '12px' }}>
							Новый шаблон создаётся черновиком: заведения увидят его после регистрации у оператора.
							Автоматические шаблоны (заказы, коды, сотрудникам) создаются только миграцией — их коды зашиты в код.
						</div>
					</form>
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100">
						<button type="button" className="btn btn-outline-warning w-100" onClick={() => setModalNew({ bool: false, item: emptyNew })}>
							Отмена
						</button>
						<button type="submit" form="smsTemplateNew" className="btn btn-primary w-100">Создать</button>
					</div>
				</Modal.Footer>
			</Modal>
			{/* MODAL NEW */}

			{/* MODAL IMPORT RESULT */}
			<Modal show={importResult.bool} animation={false} centered scrollable
				onHide={() => setImportResult({ bool: false, summary: null, errors: [] })}>
				<Modal.Header closeButton>
					<Modal.Title>{importResult.summary ? 'Файл загружен' : 'Файл не загружен'}</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '400px' }}>
					{importResult.summary &&
						<div style={{ fontSize: '14px' }}>
							<div>Новых шаблонов: <b>{importResult.summary.created}</b></div>
							<div>Изменено названий: <b>{importResult.summary.updated}</b></div>
							<div>Изменено текстов и статусов: <b>{importResult.summary.texts_changed}</b></div>
							{importResult.summary.reset_to_draft > 0 &&
								<div className="text-danger mt-2">
									Сброшено в «Черновик»: <b>{importResult.summary.reset_to_draft}</b> — текст изменился у уже
									отданного или зарегистрированного шаблона, его нужно зарегистрировать у оператора заново.
								</div>
							}
						</div>
					}
					{importResult.errors.length > 0 &&
						<>
							<div className="mb-2" style={{ fontSize: '13px' }}>Ничего не изменено. Исправьте ошибки в файле и загрузите снова:</div>
							<ul className="text-danger" style={{ fontSize: '13px' }}>
								{importResult.errors.map((error, index) => <li key={index}>{error}</li>)}
							</ul>
						</>
					}
				</Modal.Body>
			</Modal>
			{/* MODAL IMPORT RESULT */}
		</>
	)
}

export default Templates
